import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { useListTargets } from '../../../../../../../api/dags'

const useStyles = makeStyles({
  section: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#424242',
    marginBottom: 0
  },
  jsonEditor: {
    fontFamily: 'monospace',
    fontSize: 12
  }
})

function InvokeLambdaForm ({ targetIdentifier, configuration, onChangeTargetIdentifier, onChangeConfiguration }) {
  const classes = useStyles()
  const { data: targets } = useListTargets()
  const [payloadText, setPayloadText] = useState(() => {
    const payload = configuration?.invokeLambda?.payload
    return payload ? JSON.stringify(payload, null, 2) : '{}'
  })
  const [payloadError, setPayloadError] = useState(null)

  const targetOptions = useMemo(() => {
    if (!targets?.length) return []
    return targets
      .filter(t => t.targetAction === 'invokeLambda')
      .map(t => t.targetIdentifier)
  }, [targets])

  const handleTargetChange = useCallback((_event, value) => {
    onChangeTargetIdentifier(value || null)
  }, [onChangeTargetIdentifier])

  const handlePayloadChange = useCallback((e) => {
    const text = e.target.value
    setPayloadText(text)
    try {
      const parsed = JSON.parse(text)
      setPayloadError(null)
      onChangeConfiguration({
        ...configuration,
        invokeLambda: { ...(configuration?.invokeLambda ?? {}), payload: parsed }
      })
    } catch {
      setPayloadError('Invalid JSON')
    }
  }, [configuration, onChangeConfiguration])

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Lambda Configuration</div>
      <Autocomplete
        freeSolo
        options={targetOptions}
        value={targetIdentifier || ''}
        onInputChange={handleTargetChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Target Identifier (ARN)'
            variant='outlined'
            size='small'
            fullWidth
          />
        )}
      />
      <TextField
        label='Payload (JSON)'
        variant='outlined'
        size='small'
        multiline
        minRows={4}
        maxRows={12}
        value={payloadText}
        onChange={handlePayloadChange}
        error={!!payloadError}
        helperText={payloadError}
        InputProps={{ className: classes.jsonEditor }}
        fullWidth
      />
    </div>
  )
}

InvokeLambdaForm.propTypes = {
  targetIdentifier: PropTypes.string,
  configuration: PropTypes.object,
  onChangeTargetIdentifier: PropTypes.func.isRequired,
  onChangeConfiguration: PropTypes.func.isRequired
}

export default InvokeLambdaForm
